// import { Dialog, Button as MuiButton, Paper } from "@mui/material";
// import {
// 	TrashIcon,
// 	ArrowUturnLeftIcon,
// 	XMarkIcon,
// } from "@heroicons/react/24/outline";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { getApiUrl } from "../config/api";
import axios from "axios";
import { PulseLoader } from "react-spinners";
import "react-datepicker/dist/react-datepicker.css";
import DatePickerField from "../components/DatePickerField";
import LoadingOverlay from "../components/LoadingOverlay";

const TextField = React.memo(
	({
		label,
		value,
		onChange,
		className = "",
		disabled = false,
		readOnly = false,
		width = "full",
		rightIcon,
		maxLength,
		forceWhite = false,
		onClick,
	}) => {
		return (
			<div className={`${className} ${width === "full" ? "w-full" : ""}`}>
				<label className="text-sm font-medium text-gray-500">{label}</label>
				<div className="relative">
					<input
						type="text"
						value={value || ""}
						onChange={onChange}
						onClick={onClick}
						disabled={disabled}
						readOnly={readOnly}
						maxLength={maxLength}
						className={`mt-0.5 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm
            ${
							(disabled || readOnly) && !forceWhite
								? "bg-gray-100 text-gray-500"
								: "bg-white text-gray-900"
						} 
            ${rightIcon ? "pr-8" : ""}
            ${onClick ? "cursor-pointer" : ""}`}
					/>
					{rightIcon && (
						<div className="absolute inset-y-0 right-0 flex items-center pr-2 mt-0.5">
							{rightIcon}
						</div>
					)}
				</div>
			</div>
		);
	}
);

// Add this helper function at the top level
const removeLeadingZeros = (value) => {
	if (!value) return "-";
	// If the value is purely numeric, remove leading zeros
	if (/^\d+$/.test(value)) {
		return parseInt(value, 10).toString();
	}
	// Otherwise return as is
	return value;
};

const ReturnAdd = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const inqno = location.state?.inqno;
	const category = location.state?.category;	
	const [selectedItem, setSelectedItem] = useState(0);
	const [documentDate, setDocumentDate] = useState(
		new Date().toISOString().split("T")[0]
	);
	const [headerValues, setHeaderValues] = useState({
		materialSlip: "",
		docHeaderText: "",
	});

	// Change formValues to be an object with item indices as keys
	const [formValues, setFormValues] = useState({});

	// Add isSaving state to track submission status
	const [isSaving, setIsSaving] = useState(false);

	// Add alertDialog state
	const [alertDialog, setAlertDialog] = useState({
		isOpen: false,
		message: "",
		type: "success", // or "error"
	});

	// Create a showAlert function
	const showAlert = (message, type = "error") => {
		setAlertDialog({
			isOpen: true,
			message,
			type,
		});

		// Automatically close after 3 seconds for success messages
		if (type === "success") {
			setTimeout(() => {
				setAlertDialog((prev) => ({ ...prev, isOpen: false }));
			}, 3000);
		}
	};

	useEffect(() => {
		const fetchSparepartDetail = async () => {
			if (!inqno) {
				navigate("/");
				return;
			}

			try {
				setLoading(true);
				const response = await axios.get(
					getApiUrl(`/sparepart/${inqno}/return`),
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					}
				);

				// Filter out duplicate items based on inqit
				const uniqueItems = response.data.mt_item.reduce((acc, current) => {
					const isDuplicate = acc.find((item) => item.inqit === current.inqit);
					if (!isDuplicate) {
						acc.push({
							...current,
							werks: response.data.ms_header.werks_cid,
							lgort: response.data.ms_header.lgort_cid,
						});
					}
					return acc;
				}, []);

				// Update the response data with unique items
				const cleanedData = {
					...response.data,
					mt_item: uniqueItems,
					category: category,
				};

				setData(cleanedData);
			} catch (err) {
				console.error("Error fetching sparepart details:", err);
				setError("Failed to fetch sparepart details");
				if (err.response?.status === 401) {
					localStorage.removeItem("token");
					navigate("/login");
				}
			} finally {
				setLoading(false);
			}
		};

		fetchSparepartDetail();
	}, [inqno, navigate, category]);

	// Add handlers for header and form changes
	const handleHeaderChange = (field, value) => {
		setHeaderValues((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	// Update the form change handler to store values per item
	const handleFormChange = (field, value) => {
		setFormValues((prev) => ({
			...prev,
			[selectedItem]: {
				...(prev[selectedItem] || {}),
				[field]: value,
			},
		}));
	};

	// Update handleSubmit to use showAlert instead of alert
	const handleSubmit = async () => {
		if (isSaving) return; // Prevent multiple submissions
		
		try {
			// Check if any items have quantity
			const hasItemsWithQuantity = Object.values(formValues).some(
				(item) => item && item.quantity && item.quantity.trim() !== ""
			);

			if (!hasItemsWithQuantity) {
				showAlert("At least one item must have a quantity");
				return;
			}

			setIsSaving(true); // Set loading state to true before API call

			// Create payload
			const payload = {
				header: {
					plant: data.ms_header.werks_cid,
					storage_location: data.ms_header.lgort_cid,
					material_slip: headerValues.materialSlip,
					bktxt: headerValues.docHeaderText,
					document_date: documentDate,
					category: category,
					reference_inqno: inqno
				},
				details: data.mt_item
					.map((item, index) => {
						const formValue = formValues[index];
						// Only include items that have a quantity
						if (formValue && formValue.quantity && formValue.quantity.trim() !== "") {
							return {
								item: (index + 1).toString(),
								refinqit: item.inqit,
								material: item.matnr,
								material_description: item.maktx || "",
								quantity: Number(formValue.quantity),
								uom: item.uom || "",
								wempf: formValue.recipient || "",
								order: item.aufnr || "",
								text: formValue.text || "",
								charg: item.charg || "",
							};
						}
						return null;
					})
					.filter(item => item !== null) // Remove null entries
			};

			// console.log("Submitting return data:", payload);

			const response = await axios.post(
				getApiUrl(`/sparepart/${category}`),
				payload,
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);
			
			// console.log("Return response:", response.data);
			showAlert("Pengembalian berhasil disimpan", "success");
			
			// Navigate after a short delay to allow the user to see the success message
			setTimeout(() => {
				navigate("/");
			}, 1500);

		} catch (error) {
			console.error("Error submitting return:", error);
			
			// Handle error response with messages
			if (error.response?.data?.message && Array.isArray(error.response.data.message)) {
				const errorMessages = error.response.data.message;
				if (errorMessages.length > 0) {
					showAlert(
						<ul className="list-disc pl-5 space-y-1">
							{errorMessages.map((msg, index) => (
								<li key={index}>{msg}</li>
							))}
						</ul>
					);
				} else {
					showAlert("Gagal menyimpan pengembalian. Silakan coba lagi.");
				}
			} else {
				showAlert("Gagal menyimpan pengembalian. Silakan coba lagi.");
			}
			
			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setIsSaving(false); // Reset loading state regardless of success/failure
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<PulseLoader color="#2563eb" size={10} margin={4} />
			</div>
		);
	}

	if (error) {
		return <div className="text-center text-red-600 py-4">{error}</div>;
	}

	if (!data) return null;

	// Update the header section
	const headerSection = (
		<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				<TextField
					label="Plant"
					value={data.ms_header.werks_cid}
					disabled={true}
				/>
				<TextField
					label="Storage Location"
					value={data.ms_header.lgort_cid}
					disabled={true}
				/>
				<TextField label="Reference Inq No" value={removeLeadingZeros(inqno)} disabled={true} />
				<DatePickerField
					label="Document Date"
					value={documentDate}
					onChange={(date) => setDocumentDate(date.toISOString().split("T")[0])}
				/>
				<TextField
					label="Material Slip"
					value={headerValues.materialSlip}
					onChange={(e) => handleHeaderChange("materialSlip", e.target.value)}
					maxLength={16}
				/>
				<TextField
					label="Doc Header Text"
					value={headerValues.docHeaderText}
					onChange={(e) => handleHeaderChange("docHeaderText", e.target.value)}
					maxLength={25}
				/>
			</div>
		</div>
	);

	// Update the item details section
	const itemDetailsSection = (
		<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
			<h3 className="text-lg font-medium text-gray-900 mb-4">Item Details</h3>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
				<TextField
					label="Item"
					value={(selectedItem + 1).toString().padStart(2, "0")}
					disabled={true}
					width="16"
					className="opacity-75"
				/>
				<div className="sm:col-span-3">
					<TextField
						label="Material"
						value={removeLeadingZeros(data.mt_item[selectedItem]?.matnr) || ""}
						readOnly={true}
						disabled={true}
					/>
					<div className="mt-1">
						<span className="text-xs text-gray-500">
							{data.mt_item[selectedItem]?.maktx}
						</span>
					</div>
				</div>
				<div className="sm:col-span-2">
					<TextField
						label="Batch"
						value={removeLeadingZeros(data.mt_item[selectedItem]?.charg) || "-"}
						disabled={true}
					/>
				</div>
				<div className="sm:col-span-2">
					<TextField
						label="Order"
						value={data.mt_item[selectedItem]?.aufnr || "-"}
						disabled={true}
					/>
				</div>
				<div className="flex gap-2 sm:col-span-2">
					<div className="w-2/3">
						<TextField
							label="Quantity"
							value={formValues[selectedItem]?.quantity || ""}
							onChange={(e) => handleFormChange("quantity", e.target.value)}
							width="full"
						/>
					</div>
					<div className="w-1/3">
						<TextField
							label="UoM"
							value={data.mt_item[selectedItem]?.uom || ""}
							disabled={true}
							width="full"
						/>
					</div>
				</div>
				<div className="sm:col-span-2">
					<TextField
						label="Recipient"
						value={formValues[selectedItem]?.recipient || ""}
						onChange={(e) => handleFormChange("recipient", e.target.value)}
						maxLength={12}
					/>
				</div>
				<div className="sm:col-span-4">
					<TextField
						label="Text"
						value={formValues[selectedItem]?.text || ""}
						onChange={(e) => handleFormChange("text", e.target.value)}
						maxLength={50}
					/>
				</div>
			</div>
		</div>
	);

	// Update the table rows to show item-specific values
	const tableRows = data.mt_item.map((item, index) => (
		<tr
			key={item.inqit}
			onClick={() => setSelectedItem(index)}
			className={`cursor-pointer ${
				selectedItem === index
					? "bg-blue-50 hover:bg-blue-100"
					: "hover:bg-gray-50"
			}`}
		>
			<td className="px-4 py-2 whitespace-nowrap text-sm">
				{(index + 1).toString().padStart(2, "0")}
			</td>
			<td className="px-4 py-2 text-sm">
				<div>{removeLeadingZeros(item.matnr)}</div>
				<div className="text-xs text-gray-500">{item.maktx}</div>
			</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">
				{removeLeadingZeros(item.charg) || "-"}
			</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">
				{item.aufnr || "-"}
			</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">
				{formValues[index]?.quantity || "-"}
			</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">{item.uom}</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">{item.werks}</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">{item.lgort}</td>
			<td className="px-4 py-2 whitespace-nowrap text-sm">
				{formValues[index]?.recipient || "-"}
			</td>
			<td className="px-4 py-2 text-sm">{formValues[index]?.text || "-"}</td>
		</tr>
	));

	// Update the mobile cards to show item-specific values
	const mobileCards = data.mt_item.map((item, index) => (
		<div
			key={item.inqit}
			onClick={() => setSelectedItem(index)}
			className={`border rounded-lg p-4 ${
				selectedItem === index
					? "bg-blue-50 border-blue-200"
					: "bg-white hover:bg-gray-50"
			}`}
		>
			<div className="flex justify-between items-center mb-2">
				<span className="text-sm font-medium">
					Item {(index + 1).toString().padStart(2, "0")}
				</span>
				<span className="text-sm">
					{formValues[index]?.quantity || "-"} {item.uom}
				</span>
			</div>
			<div className="space-y-2">
				<div>
					<div className="text-sm font-medium">
						{removeLeadingZeros(item.matnr)}
					</div>
					<div className="text-sm text-gray-500">{item.maktx}</div>
				</div>
				<div className="grid grid-cols-2 gap-2 text-sm">
					<div>
						<span className="text-gray-500">Batch:</span>{" "}
						{removeLeadingZeros(item.charg) || "-"}
					</div>
					<div>
						<span className="text-gray-500">Plant/SLoc:</span> {item.werks}/
						{item.lgort}
					</div>
					<div>
						<span className="text-gray-500">Recipient:</span>{" "}
						{formValues[index]?.recipient || "-"}
					</div>
					<div>
						<span className="text-gray-500">Text:</span>{" "}
						{formValues[index]?.text || "-"}
					</div>
					<div>
						<span className="text-gray-500">Order:</span> {item.aufnr || "-"}
					</div>
				</div>
			</div>
		</div>
	));

	return (
		<div className="min-h-screen bg-gray-100">
			{isSaving && <LoadingOverlay />}
			<nav className="bg-white shadow-lg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						<div className="flex items-center">
							<h1 className="text-lg sm:text-xl font-bold text-gray-900">
								Pengembalian Barang {category && `(${category})`}
							</h1>
						</div>
						<div className="flex items-center space-x-2">
							<button 
								onClick={handleSubmit}
								disabled={isSaving}
								className={`px-4 py-2 text-sm text-white rounded-md ${
									isSaving 
										? "bg-gray-400 cursor-not-allowed" 
										: "bg-green-600 hover:bg-green-700"
								}`}
							>
								{isSaving ? "Saving..." : "Save"}
							</button>
							<Link
								to="/"
								className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
							>
								Cancel
							</Link>
						</div>
					</div>
				</div>
			</nav>

			<main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
				<div className="px-4 py-6 sm:px-0">
					<div className="bg-white shadow rounded-lg">
						{headerSection}
						{itemDetailsSection}
						<div className="px-4 py-5 sm:p-6">
							<h3 className="text-lg font-medium text-gray-900 mb-4">
								Items List
							</h3>
							{/* Desktop View */}
							<div className="hidden md:block overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Item
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Material
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Batch
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Order
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Quantity
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												UoM
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Plant
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												SLoc
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Recipient
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Text
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{tableRows}
									</tbody>
								</table>
							</div>
							{/* Mobile View */}
							<div className="md:hidden">
								<div className="space-y-2">{mobileCards}</div>
							</div>
						</div>
					</div>
				</div>
			</main>

			{/* Alert Dialog */}
			{alertDialog.isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div
										className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
											alertDialog.type === "success"
												? "bg-green-100"
												: "bg-red-100"
										}`}
									>
										{alertDialog.type === "success" ? (
											<svg
												className="h-6 w-6 text-green-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth="1.5"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 12.75l6 6 9-13.5"
												/>
											</svg>
										) : (
											<svg
												className="h-6 w-6 text-red-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth="1.5"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
												/>
											</svg>
										)}
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											{alertDialog.type === "success" ? "Sukses" : "Peringatan"}
										</h3>
										<div className="mt-2">
											{typeof alertDialog.message === "string" ? (
												<p className="text-sm text-gray-500">
													{alertDialog.message}
												</p>
											) : (
												<div className="text-sm text-gray-500">
													{alertDialog.message}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
										alertDialog.type === "success"
											? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
											: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
									}`}
									onClick={() =>
										setAlertDialog((prev) => ({ ...prev, isOpen: false }))
									}
								>
									OK
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ReturnAdd;
